import { ECompanyCodeConst, ECompanyTypeIdConst } from '@company/companyType/companyType.constants';
import { EOrganizationTypeCode, EOrganizationTypeId } from '@company/organizationType/organizationType.constants';
import { IProjectCompanyBase } from '@company/projectCompany/projectCompany.models';
import { AppConfigContext } from '@config/AppConfig';
import { useContext } from 'react';
import { IProjectTeamBase } from '@projectTeam/projectTeam.models';
import { useHasPermission, useIsFromTeam } from '@config/config.hooks';
import { ERolePermission } from './permission.constants';
import { IRolePermissionFlattened } from './permission.models';

type TNeededPermission = string[];

export const hasPermissionInArray = (
  permissions: IRolePermissionFlattened[] | undefined,
  permissionCode: string,
  companyTypeId?: number | string,
  organizationTypeId?: number | string,
) => {
  const companyTypeIdLocal = Number(companyTypeId ?? -1);
  const organizationTypeIdLocal = Number(organizationTypeId ?? -1);

  return (permissions || []).some(
    (permission) =>
      permission.permissionItemCode === permissionCode &&
      (!companyTypeId || permission.companyTypeId === companyTypeIdLocal) &&
      (!organizationTypeId || permission.organizationTypeId === organizationTypeIdLocal),
  );
};

export const hasPermissionInArrayByPredicate = (
  permissions: IRolePermissionFlattened[] | undefined,
  predicate: (permission: IRolePermissionFlattened) => boolean,
) => {
  return (permissions || []).some((permission) => predicate(permission));
};

export const hasAllPermissionsInArray = (
  permissions: IRolePermissionFlattened[] | undefined,
  neededPermissions: TNeededPermission | undefined,
  companyTypeId?: number | string,
  organizationTypeId?: number | string,
) => {
  if (!neededPermissions) return false;
  const companyTypeIdLocal = Number(companyTypeId ?? -1);
  const organizationTypeIdLocal = Number(organizationTypeId ?? -1);
  return neededPermissions?.every((neededPermissionCode) =>
    (permissions || []).some(
      (permission) =>
        permission.permissionItemCode === neededPermissionCode &&
        (!companyTypeId || permission.companyTypeId === companyTypeIdLocal) &&
        (!organizationTypeId || permission.organizationTypeId === organizationTypeIdLocal),
    ),
  );
};

export const hasAtLeastOnePermissionsInArray = (
  permissions: IRolePermissionFlattened[] | undefined,
  neededPermissions: TNeededPermission | undefined,
  companyTypeId?: number | string,
  organizationTypeId?: number | string,
) => {
  if (!neededPermissions) return false;
  const companyTypeIdLocal = Number(companyTypeId ?? -1);
  const organizationTypeIdLocal = Number(organizationTypeId ?? -1);

  return neededPermissions?.some((neededPermissionCode) =>
    (permissions || []).some((permission) => {
      return (
        permission.permissionItemCode === neededPermissionCode &&
        (!companyTypeId || permission.companyTypeId === companyTypeIdLocal) &&
        (!organizationTypeId || permission.organizationTypeId === organizationTypeIdLocal)
      );
    }),
  );
};

export const isBelongToCompany = (companies: IProjectCompanyBase[], companyId: number) => {
  return companies.findIndex((c) => c.id === companyId) >= 0;
};
export const isBelongToTeam = (teams: IProjectTeamBase[], teamId: number | number[]) => {
  if (typeof teamId === 'number') {
    return teams.findIndex((t) => t.id === teamId) >= 0;
  }
  if (typeof teamId === 'object') {
    const ids = teamId as number[];
    return teams.findIndex((t) => t.id && ids.includes(t.id)) >= 0;
  }
  return false;
};

export const isRestrictedAccess = (companyId?: number) => {
  const appConfigs = useContext(AppConfigContext);
  const isPermitted =
    hasAtLeastOnePermissionsInArray(
      appConfigs.configs.myPermissions,
      [
        ERolePermission.IMS_TEAM_OR_SYSTEM_DEVELOPER,
        ERolePermission.HEAD_OF_ENGINEERING,
        // REGULATORY_AUTHORITY (missing permission)
      ],
      ECompanyTypeIdConst.SYSTEM_USERS,
      EOrganizationTypeId.SYS,
    ) ||
    hasAtLeastOnePermissionsInArray(
      appConfigs.configs.myPermissions,
      [ERolePermission.VIEWER, ERolePermission.CLIENT],
      ECompanyTypeIdConst.VIEWERS,
      EOrganizationTypeId.OTH,
    );

  if (isPermitted) return false;

  if (companyId) {
    const isCompanyMember = isBelongToCompany(appConfigs.configs.myCompanies ?? [], companyId);
    if (isCompanyMember) return false;
  }

  const isLambethOrGammonSite =
    (appConfigs.configs.myCompanies ?? []).findIndex(
      (c) =>
        c.organizationType?.organizationTypeCode === EOrganizationTypeCode.SITE ||
        c.organizationType?.organizationTypeCode === EOrganizationTypeCode.LAL,
    ) >= 0;
  if (isLambethOrGammonSite) return false;
  const tenderTypeID = null; // TODO: from AppSettings

  if (tenderTypeID !== null && appConfigs.configs.project?.type?.id === tenderTypeID) {
    const projectHasExternalPartyAccessControlException = true; // TODO: from project setting
    if (projectHasExternalPartyAccessControlException) return false;
  }
  return true;
};

export const checkGeneralAccessRestricted = ({
  designerCompanyId,
  checkerCompanyId,
  teamIds,
}: {
  designerCompanyId?: number;
  checkerCompanyId?: number;
  teamIds?: number[];
}) => {
  const isDesignerCompanyRestricted = isRestrictedAccess(designerCompanyId);
  const isCheckerCompanyRestricted = isRestrictedAccess(checkerCompanyId);
  const isTeam = useIsFromTeam(teamIds ?? []);

  if (!isDesignerCompanyRestricted) return false;
  if (!isCheckerCompanyRestricted) return false;
  if (isTeam) return false;

  return true;
};

export const useIsLambethOrGammonSite = () => {
  const appConfigs = useContext(AppConfigContext);
  const isHoe = useHasPermission(ERolePermission.HEAD_OF_ENGINEERING);

  if (isHoe) return true;
  return (
    (appConfigs.configs.myCompanies ?? []).findIndex(
      (c) =>
        c.organizationType?.organizationTypeCode === EOrganizationTypeCode.SITE ||
        c.organizationType?.organizationTypeCode === EOrganizationTypeCode.LAL,
    ) >= 0
  );
};

export const isGemsSiteUser = () => {
  const appConfigs = useContext(AppConfigContext);
  const isPermitted =
    hasAtLeastOnePermissionsInArray(
      appConfigs.configs.myPermissions,
      [ERolePermission.HEAD_OF_ENGINEERING],
      ECompanyTypeIdConst.SYSTEM_USERS,
      EOrganizationTypeId.SYS,
    ) ?? false;
  if (isPermitted) return true;

  const isBelongsToGemsSite =
    (appConfigs.configs.myCompanies ?? []).findIndex(
      (c) =>
        c.code === ECompanyCodeConst.SITE_DESIGN_TEAM ||
        c.code === ECompanyCodeConst.SITE_CHECKER ||
        c.code === ECompanyCodeConst.SITE_OPERATION,
    ) >= 0;

  return isBelongsToGemsSite;
};

export const useIsViewer = () => {
  const appConfigs = useContext(AppConfigContext);

  return hasAtLeastOnePermissionsInArray(
    appConfigs.configs.myPermissions,
    [ERolePermission.VIEWER],
    ECompanyTypeIdConst.VIEWERS,
    EOrganizationTypeId.OTH,
  );
};

export const isPermittedAsSystemUser = (
  permissions: IRolePermissionFlattened[] | undefined,
  permissionCodes: string[],
) => {
  return (
    hasAtLeastOnePermissionsInArray(
      permissions,
      permissionCodes,
      ECompanyTypeIdConst.SYSTEM_USERS,
      EOrganizationTypeId.SYS,
    ) ?? false
  );
};

export const isPermittedAsSiteOperationUser = (
  permissions: IRolePermissionFlattened[] | undefined,
  permissionCodes: string[],
) => {
  return hasAtLeastOnePermissionsInArray(
    permissions,
    permissionCodes,
    ECompanyTypeIdConst.SITE_OPERATION,
    EOrganizationTypeId.SITE,
  );
};
